import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { ThemeProvider } from "@/lib/theme-context";
import { AuthProvider } from "@/lib/auth";
import { UserAuthProvider } from "@/lib/user-auth";
import { DeferredClient } from "@/components/rawan/DeferredClient";
import { CountryGate } from "@/components/rawan/CountryGate";
import { ScrollToTop } from "@/components/rawan/ScrollToTop";
import { Toaster } from "@/components/ui/sonner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-extrabold text-gradient">404</h1>
        <h2 className="mt-4 text-xl font-semibold">الصفحة غير موجودة / Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          الرابط الذي تبحث عنه غير موجود أو تم نقله.
        </p>
        <div className="mt-6">
          <Link to="/" className="btn-primary inline-flex">العودة للرئيسية</Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">حدث خطأ ما</h1>
        <p className="mt-2 text-sm text-muted-foreground">حاول مرة أخرى أو عُد للرئيسية.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button onClick={() => { router.invalidate(); reset(); }} className="btn-primary">إعادة المحاولة</button>
          <a href="/" className="px-5 py-2.5 rounded-lg border border-border hover:bg-accent">الرئيسية</a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "color-scheme", content: "dark light" },
      { name: "theme-color", content: "#ffffff", media: "(prefers-color-scheme: light)" },
      { name: "theme-color", content: "#0f172a", media: "(prefers-color-scheme: dark)" },
      { title: "Rawan UI — أفلام و مسلسلات" },
      { name: "description", content: "Rawan UI — قالب عصري لمواقع الأفلام والمسلسلات بأعلى جودة ومعايير SEO وسرعة." },
      { name: "author", content: "Rawan UI" },
      { name: "format-detection", content: "telephone=no" },
      { property: "og:site_name", content: "Rawan UI" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "ar_AR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // Google Fonts are loaded NON-blocking by FontsLoader (client) after first paint.
      // We still preconnect early so the eventual fetch is fast.
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://fonts.googleapis.com" },
      // Warm up image origins used above the fold.
      { rel: "preconnect", href: "https://image.tmdb.org", crossOrigin: "" },
      { rel: "preconnect", href: "https://images.weserv.nl", crossOrigin: "" },
      { rel: "dns-prefetch", href: "https://image.tmdb.org" },
      { rel: "dns-prefetch", href: "https://images.weserv.nl" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icons/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icons/icon-512.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/icons/apple-touch-icon.png" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Rawan UI",
          alternateName: ["روان", "Rawan"],
          inLanguage: ["ar", "en", "fr"],
          url: "https://rawan.elhizazi.me/",
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate: "https://rawan.elhizazi.me/?q={search_term_string}",
            },
            "query-input": "required name=search_term_string",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Rawan UI",
          url: "https://rawan.elhizazi.me/",
          logo: "https://rawan.elhizazi.me/icons/icon-512.png",
        }),
      },
      // Speculation Rules: prerender same-origin links on hover/touch.
      // "conservative" only triggers on pointerdown/touchstart — saves data
      // and main-thread work on slow mobile networks.
      {
        type: "speculationrules",
        children: JSON.stringify({
          prerender: [
            {
              source: "document",
              where: { and: [{ href_matches: "/*" }, { not: { href_matches: "/admin/*" } }, { not: { href_matches: "/dashboard*" } }, { not: { href_matches: "/account*" } }] },
              eagerness: "conservative",
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  // Inline, blocking script: applies saved theme + language BEFORE first paint.
  // Without this, the SSR shell hardcodes class="dark" and dir="rtl", and
  // light-mode users see a dark flash from the preloader until React mounts
  // and ThemeProvider's useEffect runs.
  const themeBoot = `(function(){try{
    var d=document.documentElement;
    var m=localStorage.getItem('rawan:mode')||'system';
    var dark = m==='dark' ? true
             : m==='light' ? false
             : (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
    d.classList.toggle('dark', !!dark);
    var l=localStorage.getItem('rawan:language')||localStorage.getItem('rawan:lang'); if(l==='fe')l='fr';
    if(l==='ar'||l==='en'||l==='fr'){ d.lang=l; d.dir=(l==='ar'?'rtl':'ltr'); }
    d.style.background = dark ? '#0f172a' : '#ffffff';
    d.style.colorScheme = dark ? 'dark' : 'light';
  }catch(e){}})();`;
  return (
    <html lang="ar" dir="rtl" className="dark" suppressHydrationWarning>
      <head>
        <script dangerouslySetInnerHTML={{ __html: themeBoot }} />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useEffect(() => {
    const id = window.setTimeout(() => {
      import("@/lib/smart-notifications").then((m) => m.startSmartNotifications()).catch(() => {});
    }, 10000);
    return () => window.clearTimeout(id);
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <UserAuthProvider>
            <CountryGate>
              <Outlet />
            </CountryGate>
            <DeferredClient loader={() => import("@/components/rawan/TickerBar")} delayMs={8000} />
            <DeferredClient loader={() => import("@/components/rawan/FontsLoader")} delayMs={2500} />
            <DeferredClient loader={() => import("@/components/rawan/PwaInstallPrompt")} delayMs={3000} />
            <DeferredClient loader={() => import("@/components/rawan/NotificationsPrompt")} delayMs={10000} />
            <DeferredClient loader={() => import("@/components/rawan/ServiceWorkerRegister")} delayMs={2000} />
            <DeferredClient loader={() => import("@/components/rawan/CookieConsent")} delayMs={1500} />
            <DeferredClient loader={() => import("@/components/rawan/AdHead")} delayMs={12000} />
            <DeferredClient loader={() => import("@/components/telegram/TelegramProvider")} delayMs={500} />
            <ScrollToTop />
            <Toaster position="top-center" richColors closeButton />
          </UserAuthProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
